// "Where you use it": the surfaces that exist in this repo, each with an honest status.
// Sources: apps/cli/collect.py (commands), apps/web/src/components/* (CapsulesList,
// WorkspaceBrowser, CapsuleContents, KGForceGraph, CapsuleCompare, SendCapsule),
// apps/extension/manifest.json + background.js + README ("until Chrome Web Store ships"),
// synapse-connect/backend/src/routes/collectCapsuleRoutes.ts (API + workspace-bound keys),
// apps/desktop/src-tauri/tauri.conf.json (updater pubkey still a placeholder: no release).
import { motion } from "motion/react";
import { Terminal, LayoutDashboard, Puzzle, Braces, Monitor } from "lucide-react";
import { useLandingTheme } from "./theme";

type Status = "available" | "beta" | "developer" | "building";

const STATUS: Record<Status, { label: string; color: string }> = {
  available: { label: "Available", color: "#10B981" },
  beta: { label: "Closed beta", color: "#10B981" },
  developer: { label: "Developer mode", color: "#F2C94C" },
  building: { label: "In development", color: "#F2C94C" },
};

const SURFACES: Array<{ icon: typeof Terminal; name: string; status: Status; desc: string; detail: string }> = [
  {
    icon: Terminal,
    name: "Command line",
    status: "beta",
    desc: "The engine: the collect command, installed with pipx from the wheel Qodoonect sends you. Build a wiki, search it and send capsules; post-quantum encryption (--encrypt) is experimental.",
    detail: "init · ingest · search · status · show · send-to-hive · hive-list · keys (experimental)",
  },
  {
    icon: LayoutDashboard,
    name: "Web app",
    status: "beta",
    desc: "Your capsules on a timeline and by workspace. Open one to read its sources and concepts and explore its knowledge graph; compare two versions; send text files from the browser.",
    detail: "Sign in on this site",
  },
  {
    icon: Puzzle,
    name: "Browser extension",
    status: "developer",
    desc: "For Chrome (Manifest V3). Save the page, a selection or a link from the popup, the right-click menu or Ctrl+Shift+S; article text through Mozilla Readability.",
    detail: "Loaded unpacked · not in the Chrome Web Store yet",
  },
  {
    icon: Braces,
    name: "HTTP API",
    status: "beta",
    desc: "Send a capsule, list and read capsules, fetch the payload. An access key only reaches the workspaces it is bound to.",
    detail: "POST /api/collect/capsule · GET /api/collect/capsule",
  },
  {
    icon: Monitor,
    name: "Desktop app",
    status: "building",
    desc: "A Tauri window around the web app, with native drag-and-drop of files and a terminal tab that drives the CLI.",
    detail: "Not released · no signed build yet",
  },
];

export function FeaturesSection() {
  const { tone } = useLandingTheme();
  return (
    <section id="features" aria-labelledby="features-title" className="px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <header className="mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Where you use it</p>
          <h2 id="features-title" className="text-5xl md:text-7xl font-serif font-light leading-[0.95]">
            One format.<br />
            <span className="italic text-white/65">Every front door.</span>
          </h2>
          <p className="mt-6 text-base font-light text-white/70 max-w-2xl leading-relaxed">
            The CLI, the web app and the extension all write the same capsule format,
            <span className="font-mono text-[0.9em]"> collect.capsule.v1</span>, to the same HIVE.
          </p>
        </header>

        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {SURFACES.map((s, idx) => {
            const Icon = s.icon;
            const st = STATUS[s.status];
            return (
              <motion.li
                key={s.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="glass-panel rounded-2xl p-7 md:p-8 flex flex-col"
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Icon size={24} strokeWidth={1.4} style={{ color: tone("#34D399") }} aria-hidden="true" />
                  <span className="collect-tag" style={{ color: tone(st.color), borderColor: `${st.color}55` }}>
                    {st.label}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif font-light mb-3">{s.name}</h3>
                <p className="text-sm font-light text-white/70 leading-relaxed mb-6">{s.desc}</p>
                <p className="mt-auto font-mono text-[11px] tracking-wide text-white/60 break-words">{s.detail}</p>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
